// Browser-side image compression.
//
// Why: a 6 MB Aadhaar photo from a phone shrinks to ~400 KB at quality 0.85
// with no perceptible loss for ID scans. We do this BEFORE the request hits
// the server, so our 10 MB upload cap stops failing on phone uploads, the
// 13 MB-per-row MongoDB ceiling is never approached, and the per-employee
// document quota lasts ~10x longer.
//
// What gets compressed:
//   - `image/jpeg`, `image/png`, `image/webp` → JPEG at quality 0.85
//   - everything else (PDF, HEIC, doc/xlsx) → returned untouched
//   - any file smaller than the threshold (default 500 KB) → returned untouched
//     (compression rarely helps below this size and sometimes makes it bigger)
//
// Why we keep the smaller of (original, compressed) and not always replace:
//   - PNG with sharp edges (logos, screenshots) can re-encode LARGER as JPEG
//   - We never want to inflate. If compressed >= original, return original.

const MAX_DIMENSION_PX = 2000;        // long edge cap; preserves aspect ratio
const QUALITY = 0.85;                  // JPEG quality (0–1)
const SKIP_BELOW_BYTES = 500 * 1024;   // <500 KB → no point compressing

/**
 * Hard upload limit ENFORCED AFTER compression. Files that exceed this
 * (typically uncompressible PDFs from old scanners that bake images at 600
 * DPI) are rejected at the UI layer with a clear error. The backend
 * mirrors this number in `upload_document_file` so any non-browser client
 * (Postman, curl, mobile app) gets the same answer.
 *
 * Why 5 MB:
 *   - Compressed phone photo of any ID ≈ 200-500 KB
 *   - A 50-page text PDF ≈ 1-2 MB
 *   - A scanned PDF that's 5 MB+ is almost always a 600 DPI image-based PDF
 *     which we want HR to re-export at 200 DPI before uploading.
 *   - Keeps MongoDB row size well under the 16 MB BSON ceiling.
 */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

const COMPRESSIBLE_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);

/**
 * Returns a File. If compression isn't applicable or would make the file
 * bigger, returns the original `file` unchanged.
 */
export async function maybeCompressImage(file) {
  if (!file) return file;
  if (file.size < SKIP_BELOW_BYTES) return file;
  if (!COMPRESSIBLE_TYPES.has(file.type)) return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // Some old browsers can't decode the format. Fall back gracefully.
    return file;
  }

  const longEdge = Math.max(bitmap.width, bitmap.height);
  const scale = longEdge > MAX_DIMENSION_PX ? MAX_DIMENSION_PX / longEdge : 1;
  const targetW = Math.round(bitmap.width * scale);
  const targetH = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  // Release bitmap memory ASAP — phones with low RAM can OOM otherwise.
  try { bitmap.close(); } catch { /* not all browsers expose close() */ }

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', QUALITY)
  );

  if (!blob || blob.size >= file.size) {
    // Re-encoding actually grew the file (small icon, very sparse PNG).
    // Keep the original.
    return file;
  }

  // Preserve a meaningful filename. We swap the extension to .jpg since the
  // backend uses content-type for serving and we explicitly produced JPEG.
  const baseName = (file.name || 'image').replace(/\.[^/.]+$/, '');
  return new File([blob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

/** Human-readable byte formatter, mirroring DocumentManagement.formatFileSize */
export function fmtBytes(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
